import { ReactNode } from "react";
import dayjs from "dayjs";
import { Box, Chip, Tooltip } from "@mui/material";
import { Circle, VerifiedUser } from "@mui/icons-material";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid-premium";

/*
 * Progress, sign-off and priority cell rendering shared by the overview grids.
 *
 * The counterparty and broker column hooks each carry their own copy of this logic and have already
 * drifted from one another in small ways. Rather than add a third copy for countries, the country hook
 * imports from here. The two older hooks are worth migrating onto this the next time either is touched.
 */

export const UNASSIGNED = "Unassigned";

export type ProgressLabel = "Completed" | "In Progress" | "Not Initiated";

export const normalizeProgress = (
  progress: string | null | undefined
): ProgressLabel => {
  switch (progress?.toLowerCase()) {
    case "completed":
      return "Completed";
    case "in progress":
      return "In Progress";
    default:
      return "Not Initiated";
  }
};

const getProgressIcon = (progress: ProgressLabel) => {
  switch (progress) {
    case "Completed":
      return (
        <Circle sx={{ fill: (t) => t.palette.NICE_GREEN }} fontSize="small" />
      );
    case "In Progress":
      return <Circle sx={{ fill: "#529CCF" }} fontSize="small" />;
    case "Not Initiated":
    default:
      return <Circle sx={{ fill: "#C6C6C6" }} fontSize="small" />;
  }
};

export const renderProgressCell = (status: ProgressLabel) => (
  <Tooltip title={status}>
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      fontSize="12px"
      lineHeight={1}
      gap={0.5}
    >
      {getProgressIcon(status)}
      {status}
    </Box>
  </Tooltip>
);

export type SignOffLabel =
  | "Not started"
  | "Draft"
  | "In Progress"
  | "Rejected"
  | "Completed";

// Ordered by where the state sits in the sign-off flow, so sorting the column
// walks the pipeline instead of sorting the labels alphabetically.
export const SIGN_OFF_ORDER: SignOffLabel[] = [
  "Not started",
  "Draft",
  "In Progress",
  "Rejected",
  "Completed"
];

// Entities that never had a sign-off opened come back null, which reads as "Not started".
export const normalizeSignOff = (
  status: string | null | undefined
): SignOffLabel => {
  switch (status?.toLowerCase()) {
    case "draft":
      return "Draft";
    case "in_progress":
      return "In Progress";
    case "rejected":
      return "Rejected";
    case "completed":
      return "Completed";
    default:
      return "Not started";
  }
};

const getSignOffIcon = (status: SignOffLabel) => {
  switch (status) {
    case "Completed":
      return (
        <Circle sx={{ fill: (t) => t.palette.NICE_GREEN }} fontSize="small" />
      );
    case "Rejected":
      return <Circle sx={{ fill: "#D21919" }} fontSize="small" />;
    case "In Progress":
      return <Circle sx={{ fill: "#529CCF" }} fontSize="small" />;
    case "Draft":
      return <Circle sx={{ fill: "#9AA0A6" }} fontSize="small" />;
    case "Not started":
    default:
      return <Circle sx={{ fill: "#C6C6C6" }} fontSize="small" />;
  }
};

export const renderSignOffCell = (status: SignOffLabel) => (
  <Tooltip title={status}>
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      fontSize="12px"
      lineHeight={1}
      gap={0.5}
    >
      {getSignOffIcon(status)}
      {status}
    </Box>
  </Tooltip>
);

export type ReadinessLabel = "Not started" | "Draft" | "In Progress" | "Ready";

export const READINESS_ORDER: ReadinessLabel[] = [
  "Not started",
  "Draft",
  "In Progress",
  "Ready"
];

// The summary column is null when readiness is not required (yet: it is one of the last steps),
// and on rows not saved since the column was added (no backfill yet). Both read as "Not started",
// which the cell renders blank so the rows that have a readiness stand out. The label stays the
// value so it can still be filtered on and exports as text.
export const normalizeReadiness = (
  status: string | null | undefined
): ReadinessLabel => {
  switch (status?.toLowerCase()) {
    case "draft":
      return "Draft";
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Ready";
    default:
      return "Not started";
  }
};

const getReadinessIcon = (status: ReadinessLabel) => {
  switch (status) {
    case "Ready":
      return (
        <Circle sx={{ fill: (t) => t.palette.NICE_GREEN }} fontSize="small" />
      );
    case "In Progress":
      return <Circle sx={{ fill: "#529CCF" }} fontSize="small" />;
    case "Draft":
    default:
      return <Circle sx={{ fill: "#9AA0A6" }} fontSize="small" />;
  }
};

// Operational readiness (ADHO-551) on the counterparty and broker grids. "Ready" means Risk,
// Treasury and Finance have all signed that the entity is set up to trade.
export const operationalReadinessColumn = <R extends GridValidRowModel>(
  getStatus: (row: R) => string | null | undefined
): GridColDef<R> => ({
  field: "operationalReadinessStatus",
  headerName: "Operational Readiness",
  width: 130,
  sortable: true,
  filterable: true,
  type: "singleSelect",
  valueOptions: READINESS_ORDER,
  valueGetter: (_value, row) => normalizeReadiness(getStatus(row)),
  sortComparator: (a: ReadinessLabel, b: ReadinessLabel) =>
    READINESS_ORDER.indexOf(a) - READINESS_ORDER.indexOf(b),
  renderCell: (params) =>
    params.value === "Not started" ? null : (
      <Tooltip title={params.value}>
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          fontSize="12px"
          lineHeight={1}
          gap={0.5}
        >
          {getReadinessIcon(params.value)}
          {params.value}
        </Box>
      </Tooltip>
    ),
  renderHeader: () => (
    <Tooltip title="Operational readiness: ready for trading">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <VerifiedUser fontSize="small" />
        OR
      </Box>
    </Tooltip>
  )
});

// A date column for the overview grids, shown as DD/MM/YYYY. Typed "date" so the column filter
// offers before / after and the Excel export writes a real date cell rather than text. Excel
// ignores valueFormatter; ExportMenu applies the same format there.
export const dateColumn = <R extends GridValidRowModel>(
  field: string,
  headerName: string,
  getValue: (row: R) => string | null | undefined,
  icon: ReactNode
): GridColDef<R> => ({
  field,
  headerName,
  width: 150,
  sortable: true,
  filterable: true,
  type: "date",
  valueGetter: (_value, row) => {
    const value = getValue(row);
    return value ? new Date(value) : null;
  },
  valueFormatter: (value: Date | null) =>
    value ? dayjs(value).format("DD/MM/YYYY") : "",
  renderHeader: () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      {headerName}
    </Box>
  )
});

export const renderPriorityCell = (urgent: boolean | null | undefined) =>
  urgent === true ? (
    <Chip
      label="URGENT"
      size="small"
      variant="filled"
      sx={{ backgroundColor: "#D21919", color: "#FFF" }}
    />
  ) : null;
